import { useContext, useState } from 'react'
import { BannerAlertBox } from '@entur/alert'
import { Loader } from '@entur/loader'
import { ActionChip } from '@entur/chip'
import { AddIcon } from '@entur/icons'
import { FamilyMember, FetchState } from '../../api-clients/api-types'
import { NoResult } from '../no-result/no-result'
import EditFamilyMemberModal from './edit-family-member-modal'
import DeleteFamilyMemberModal from './delete-family-member-modal'
import FamilyMemberCard from './family-member'
import noFamilyMembersImage from './no-family-members.jpg'
import { ModalState } from '../../types'
import { useEmployee } from '../../contexts/employee-context'
import { useFamilyMembers } from '../../contexts/family-members-context'
import { FamilyMembersTicketRightsSyncCards } from '../ticket-rights/sync/ticket-right-sync-card'
import { useClientInfo } from '../../contexts/client-info-context'
import { ReadOnlyContext } from '../../contexts/read-only-context'

const FamilyMemberTab = () => {
  const { hasSensitiveAccess } = useClientInfo()
  const { readOnly } = useContext(ReadOnlyContext)
  const { employee } = useEmployee()
  const { familyMembersResponse, setFamilyMember, fetchFamilyMembers } = useFamilyMembers()
  const [modalSate, setModalState] = useState(ModalState.CLOSED)
  const [activeFamilyMember, setActiveFamilyMember] = useState<FamilyMember>()
  const closeModalAndUpdate = (shouldUpdate: boolean) => {
    setModalState(ModalState.CLOSED)
    if (shouldUpdate) {
      fetchFamilyMembers({ id: employee.id })
    }
  }

  return (
    <div className="family-member-tab" data-cy="family-member-tab">
      {familyMembersResponse.status === FetchState.LOADING && <Loader>Behandler</Loader>}
      {familyMembersResponse.status !== FetchState.LOADING && (
        <>
          {!readOnly && hasSensitiveAccess === true && (
            <ActionChip
              style={{ marginBottom: '1rem' }}
              data-cy="new-family-member-button"
              onClick={() => {
                setActiveFamilyMember(undefined)
                setModalState(ModalState.EDIT)
              }}
            >
              <AddIcon />
              Legg til familiemedlem
            </ActionChip>
          )}
          <EditFamilyMemberModal
            open={modalSate === ModalState.EDIT}
            employee={employee}
            familyMember={activeFamilyMember}
            close={closeModalAndUpdate}
          />
        </>
      )}
      {familyMembersResponse.status === FetchState.SUCCESS && familyMembersResponse.data?.length > 0 && (
        <>
          <FamilyMembersTicketRightsSyncCards />
          {activeFamilyMember && (
            <DeleteFamilyMemberModal
              open={modalSate === ModalState.DELETE}
              familyMember={activeFamilyMember}
              close={closeModalAndUpdate}
            />
          )}
          {[...familyMembersResponse.data]
            .sort((a, b) => (a.id > b.id ? -1 : 1))
            .map((fm) => (
              <FamilyMemberCard
                changeEvent={(changedFamilyMember, modalState) => {
                  setActiveFamilyMember(changedFamilyMember)
                  setModalState(modalState)
                  setFamilyMember(changedFamilyMember)
                }}
                key={fm.ticketHolderCode}
                familyMember={fm}
              />
            ))}
        </>
      )}
      {familyMembersResponse.status === FetchState.SUCCESS && familyMembersResponse.data.length === 0 && (
        <NoResult
          imgSrc={noFamilyMembersImage}
          imageHeight="300px"
          title="Her var det visst ingen ennå!"
          text="Familiemedlemmer som blir registrert vil havne i denne fanen."
        />
      )}
      {familyMembersResponse.status === FetchState.ERROR && (
        <BannerAlertBox
          variant="error"
          title="Kan ikke hente familiemedlemmer"
          closable={false}
          style={{ marginBottom: '1em' }}
        >
          Det har skjedd en teknisk feil, ta kontakt med Entur support
        </BannerAlertBox>
      )}
    </div>
  )
}

export default FamilyMemberTab
